import { useEffect } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Navigation from "@/components/Navigation";
import Footer from "@/components/ui/footer";
import {
  Check,
  ArrowRight,
  Sparkles,
  Clock,
  Shield,
  Gift,
  Zap,
  Crown,
  DollarSign,
  Calendar,
  CreditCard,
  RefreshCw,
  HelpCircle,
  MessageCircle,
  Phone,
  Mail,
  Image,
  HardDrive,
  Code,
  Building,
  Globe,
  Server,
  Camera,
  User,
  BookOpen,
} from "lucide-react";
import { TeddyBear } from "@/components/icons/TeddyBear";
import { motion } from "framer-motion";

const pricingPlans = [
  {
    name: "Premium",
    price: "$6.99",
    period: "per month",
    description: "Most popular choice",
    features: [
      "50 AI-generated illustrations",
      "1 GB storage",
      "Unlimited story generation",
      "AI-powered narration",
      "Multi-voice character narration",
      "10 languages supported",
      "Story playlists with auto-play",
      "Advanced customization",
      "1 FREE child profile (additional $1.99 each one-time)",
      "PDF downloads & printing",
    ],
    cta: "Start Free Trial",
    popular: true,
    savings: null,
    color: "from-purple-500 to-pink-500",
  },
  {
    name: "Premium Yearly",
    price: "$69.99",
    period: "per year",
    description: "Best value - save $13.89!",
    features: [
      "Everything in Premium",
      "1 FREE child profile (additional $1.99 each one-time)",
      "Multi-voice character narration",
      "10 languages supported",
      "Story playlists with auto-play",
      "Save $13.89 per year (~17% discount)",
      "Exclusive seasonal stories",
      "Advanced customization options",
      "Early access to new features",
    ],
    cta: "Start Free Trial",
    popular: false,
    savings: "Save 17%",
    color: "from-teal-500 to-cyan-500",
  },
];

const characterStudioPacks = [
  {
    name: "Character Studio Monthly",
    price: "$2.99",
    period: "per month",
    description: "Premium photo-to-cartoon feature",
    features: [
      "Transform photos into cartoon characters",
      "Unlimited character creation",
      "High-quality AI transformation",
      "Character gallery management",
      "Story integration",
      "Cancel anytime",
      "Works with any plan",
    ],
    icon: Camera,
    popular: true,
    savings: null,
    color: "from-rose-500 to-pink-500",
  },
  {
    name: "Character Studio Annual",
    price: "$29.99",
    period: "per year",
    description: "Best value - save $5.89!",
    features: [
      "Everything in Monthly",
      "Transform photos into cartoon characters",
      "Unlimited character creation",
      "High-quality AI transformation",
      "Save $5.89 per year (~17% discount)",
      "Priority character processing",
      "Works with any plan",
    ],
    icon: Camera,
    popular: false,
    savings: "Save 17%",
    color: "from-rose-600 to-pink-600",
  },
];

const illustrationPacks = [
  {
    name: "+25 Illustrations Monthly",
    price: "$1.99",
    period: "per month",
    description: "Additional AI-generated illustrations",
    features: [
      "25 high-quality illustrations per month",
      "Perfect for extra stories",
      "Recurring monthly subscription",
      "Cancel anytime",
      "Works with all plans",
    ],
    icon: Image,
  },
  {
    name: "+100 Illustrations Monthly",
    price: "$4.99",
    period: "per month",
    description: "Popular choice for active users",
    features: [
      "100 high-quality illustrations per month",
      "Best value per illustration",
      "Recurring monthly subscription",
      "Cancel anytime",
      "Works with all plans",
    ],
    icon: Image,
  },
  {
    name: "+500 Illustrations Monthly",
    price: "$14.99",
    period: "per month",
    description: "Great for power users",
    features: [
      "500 high-quality illustrations per month",
      "Significant savings",
      "Recurring monthly subscription",
      "Cancel anytime",
      "Works with all plans",
    ],
    icon: Image,
  },
  {
    name: "+2000 Illustrations Monthly",
    price: "$39.99",
    period: "per month",
    description: "Ultimate illustration pack",
    features: [
      "2000 high-quality illustrations per month",
      "Maximum value",
      "Recurring monthly subscription",
      "Cancel anytime",
      "Works with all plans",
    ],
    icon: Image,
  },
];

const storagePacks = [
  {
    name: "+5 GB Storage Monthly",
    price: "$1.99",
    period: "per month",
    description: "Additional cloud storage",
    features: [
      "5 GB extra storage",
      "Perfect for more stories",
      "Recurring monthly subscription",
      "Cancel anytime",
      "Works with all plans",
    ],
    icon: HardDrive,
  },
  {
    name: "+10 GB Storage Monthly",
    price: "$3.99",
    period: "per month",
    description: "Popular storage upgrade",
    features: [
      "10 GB extra storage",
      "Best value per GB",
      "Recurring monthly subscription",
      "Cancel anytime",
      "Works with all plans",
    ],
    icon: HardDrive,
  },
  {
    name: "+20 GB Storage Monthly",
    price: "$6.99",
    period: "per month",
    description: "Great for families",
    features: [
      "20 GB extra storage",
      "Significant savings",
      "Recurring monthly subscription",
      "Cancel anytime",
      "Works with all plans",
    ],
    icon: HardDrive,
  },
  {
    name: "Unlimited Storage Monthly",
    price: "$14.99",
    period: "per month",
    description: "Never worry about storage again",
    features: [
      "Unlimited cloud storage",
      "Maximum convenience",
      "Recurring monthly subscription",
      "Cancel anytime",
      "Works with all plans",
    ],
    icon: HardDrive,
  },
];

const childProfilePacks = [
  {
    name: "+1 Child Profile Monthly",
    price: "$1.99",
    period: "per month",
    description: "Add one more child profile slot",
    features: [
      "One additional child profile slot",
      "Save child details (name, age, birthday, gender)",
      "Upload photo for cartoon avatar generation",
      "Separate story library",
      "Personalized story preferences",
      "Character appears in illustrations",
      "Recurring monthly subscription",
      "Cancel anytime - Works with all plans",
    ],
    icon: TeddyBear,
  },
  {
    name: "+3 Child Profiles Monthly",
    price: "$4.99",
    period: "per month",
    description: "Best value for larger families",
    features: [
      "Three additional child profile slots",
      "Save $0.98 vs buying individually",
      "All child profile features included",
      "Photo upload & avatar generation for each",
      "Separate libraries per child",
      "Recurring monthly subscription",
      "Cancel anytime - Works with all plans",
    ],
    icon: TeddyBear,
  },
];

const whiteLabelPlans = [
  {
    name: "Starter",
    price: "$99",
    period: "per month",
    description: "Perfect for small businesses",
    features: [
      "White-label branding",
      "Custom domain support",
      "Basic API access",
      "Up to 100 stories/month",
      "Email support",
      "Custom themes",
      "Basic analytics",
      "SSL certificate included",
    ],
    cta: "Contact Sales",
    popular: false,
    savings: null,
    color: "from-blue-500 to-cyan-500",
  },
  {
    name: "Professional",
    price: "$299",
    period: "per month",
    description: "Most popular for growing businesses",
    features: [
      "Everything in Starter",
      "Advanced API access",
      "Up to 1000 stories/month",
      "Priority support",
      "Advanced analytics",
      "Custom integrations",
      "Multi-language support",
      "Dedicated account manager",
    ],
    cta: "Contact Sales",
    popular: true,
    savings: null,
    color: "from-purple-500 to-pink-500",
  },
  {
    name: "Enterprise",
    price: "$999",
    period: "per month",
    description: "For large organizations",
    features: [
      "Everything in Professional",
      "Unlimited stories",
      "Custom development",
      "24/7 phone support",
      "Advanced security features",
      "Custom SLA",
      "On-premise deployment option",
      "White-label mobile apps",
    ],
    cta: "Contact Sales",
    popular: false,
    savings: "Best Value",
    color: "from-amber-500 to-orange-500",
  },
];

const apiPlans = [
  {
    name: "Developer",
    price: "Custom",
    period: "pricing",
    description: "Perfect for testing and development",
    features: [
      "500 API calls per month",
      "Basic documentation",
      "Community support",
      "Rate limiting",
      "JSON responses",
      "Basic authentication",
      "Test environment access",
      "SDK downloads",
    ],
    cta: "Contact Sales",
    popular: false,
    savings: null,
    color: "from-gray-500 to-gray-700",
  },
  {
    name: "Standard",
    price: "$49",
    period: "per month",
    description: "Great for small applications",
    features: [
      "5,000 API calls per month",
      "Priority support",
      "Advanced documentation",
      "Higher rate limits",
      "Webhook support",
      "API analytics",
      "Production environment",
      "Email support",
    ],
    cta: "Contact Sales",
    popular: true,
    savings: null,
    color: "from-green-500 to-emerald-500",
  },
  {
    name: "Pro",
    price: "$199",
    period: "per month",
    description: "For growing applications",
    features: [
      "25,000 API calls per month",
      "Priority support",
      "Advanced features",
      "Custom rate limits",
      "Advanced webhooks",
      "Detailed analytics",
      "Phone support",
      "Custom integrations",
    ],
    cta: "Contact Sales",
    popular: false,
    savings: null,
    color: "from-blue-500 to-cyan-500",
  },
  {
    name: "Enterprise",
    price: "$499",
    period: "per month",
    description: "For large-scale applications",
    features: [
      "100,000 API calls per month",
      "24/7 dedicated support",
      "Custom features",
      "Unlimited rate limits",
      "Custom webhooks",
      "Advanced analytics",
      "Dedicated account manager",
      "SLA guarantee",
    ],
    cta: "Contact Sales",
    popular: false,
    savings: "Best Value",
    color: "from-purple-500 to-pink-500",
  },
];

const faqs = [
  {
    question: "How do child profiles work?",
    answer:
      "Child profiles let you save each child's details (name, age, birthday, gender, language) and upload their photo. Our AI automatically generates a cartoon avatar from their photo, which appears in story illustrations. Your first child profile is FREE with any subscription plan.",
  },
  {
    question: "Is the $1.99 for additional child profiles a monthly charge?",
    answer:
      "Yes! Additional child profile slots are a RECURRING subscription of $1.99 per month. Your first child profile is always FREE with your subscription. You can cancel the child profile subscription anytime.",
  },
  {
    question:
      "What's the difference between child profile photos and Character Studio?",
    answer:
      "Child profile photo upload (FREE) creates one cartoon avatar per profile that represents your child in stories. Character Studio ($2.99/month add-on) is a separate premium service that lets you create unlimited custom characters with advanced AI features. Both are great options depending on your needs!",
  },
  {
    question: "Can I add more child profiles later?",
    answer:
      "Absolutely! You can subscribe to additional child profile slots anytime for $1.99/month each (recurring). Each additional slot includes all features: photo upload, avatar generation, separate library, and personalized settings. Cancel anytime.",
  },
  {
    question: "How does Character Studio pricing work?",
    answer:
      "Character Studio is a premium add-on subscription available for $2.99/month or $29.99/year (save 17%). It works with any base plan and gives you unlimited photo-to-cartoon transformations and character creation.",
  },
  {
    question: "How do illustration and storage packs work?",
    answer:
      "Illustration and storage packs are recurring monthly subscriptions. They work with all plans and give you additional resources beyond your base monthly limits. Each month, your pack credits renew automatically. You can subscribe to multiple packs and cancel anytime.",
  },
  {
    question: "What's the difference between monthly and yearly Premium plans?",
    answer:
      "Both plans include the same features (50 illustrations, 1 GB storage), but the yearly plan costs $69.99/year instead of $6.99/month, saving you $13.89 per year (about 17% discount).",
  },
  {
    question: "What is White-Label Licensing?",
    answer:
      "White-label licensing allows businesses to rebrand our platform with their own domain, logo, and branding. Perfect for agencies, educational institutions, or companies wanting to offer storytelling as part of their services.",
  },
  {
    question: "How does API licensing work?",
    answer:
      "API licensing gives developers access to our AI storytelling capabilities through REST APIs. Plans range from developer (500 calls/month) to enterprise (100,000 calls/month) with different features and support levels.",
  },
  {
    question: "Can I upgrade or downgrade my plan anytime?",
    answer:
      "Yes! You can change your plan at any time. Upgrades take effect immediately, and downgrades take effect at the next billing cycle. We'll prorate any billing differences.",
  },
  {
    question: "What payment methods do you accept?",
    answer:
      "We accept all major credit cards (Visa, MasterCard, American Express), PayPal, and Apple Pay. All payments are processed securely through Stripe.",
  },
  {
    question: "Do you offer refunds?",
    answer:
      "We offer a 30-day money-back guarantee for all paid plans. If you're not satisfied with our service, contact us within 30 days for a full refund.",
  },
];

export default function PricingPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-teal-50">
      <Navigation />

      {/* Hero Section */}
      <section className="py-12 sm:py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Badge className="mb-4 bg-gradient-to-r from-purple-600 to-teal-600 text-white text-sm">
                💰 Simple, Transparent Pricing
              </Badge>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-purple-600 via-teal-600 to-amber-600 bg-clip-text text-transparent leading-tight">
                Choose the Perfect Plan for Your Family
              </h1>
              <p className="text-lg sm:text-xl text-gray-700 mb-6 sm:mb-8 leading-relaxed max-w-4xl mx-auto">
                Start with a 7-day free trial. No credit card required. Cancel
                anytime. All plans include unlimited story generation and access
                to all core features. Add Character Studio for custom cartoon
                characters!
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Plans Section */}
      <section className="py-12 sm:py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent">
              Choose the Plan That Works Best for Your Family
            </h2>
            <p className="text-lg sm:text-xl text-gray-600">
              Start with a 7-day free trial • All plans include unlimited story
              generation • Character Studio available as add-on
            </p>
          </div>
          <div className="grid sm:grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 max-w-4xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card
                  className={`h-full relative ${plan.popular ? "ring-2 ring-purple-500 shadow-xl scale-105" : ""}`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <Badge className="bg-gradient-to-r from-purple-600 to-teal-600 text-white px-4 py-1">
                        Most Popular
                      </Badge>
                    </div>
                  )}
                  {plan.savings && (
                    <div className="absolute -top-2 -right-2">
                      <Badge
                        variant="secondary"
                        className="bg-green-100 text-green-800"
                      >
                        {plan.savings}
                      </Badge>
                    </div>
                  )}
                  <CardHeader className="text-center">
                    <div
                      className={`w-16 h-16 bg-gradient-to-r ${plan.color} rounded-lg flex items-center justify-center mx-auto mb-4`}
                    >
                      <Crown className="h-8 w-8 text-white" />
                    </div>
                    <CardTitle className="text-2xl">{plan.name}</CardTitle>
                    <div className="mt-4">
                      <span className="text-4xl font-bold">{plan.price}</span>
                      <span className="text-gray-600 ml-2">{plan.period}</span>
                    </div>
                    <CardDescription className="text-base mt-2">
                      {plan.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3 mb-8">
                      {plan.features.map((feature, featureIndex) => (
                        <li
                          key={featureIndex}
                          className="flex items-center gap-3"
                        >
                          <Check className="h-5 w-5 text-green-600 flex-shrink-0" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Link href="/auth">
                      <Button
                        className={`w-full ${plan.popular ? "bg-gradient-to-r from-purple-600 to-teal-600 hover:from-purple-700 hover:to-teal-700" : ""}`}
                        variant={plan.popular ? "default" : "outline"}
                        size="lg"
                      >
                        {plan.cta}
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-8 sm:mt-12">
            <p className="text-gray-600 mb-4 text-sm sm:text-base">
              All plans include a 7-day free trial • Cancel anytime • No setup
              fees
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 text-sm text-gray-500">
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4" />
                <span>Secure payment</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>Instant access</span>
              </div>
              <div className="flex items-center gap-2">
                <Gift className="h-4 w-4" />
                <span>Gift subscriptions available</span>
              </div>
            </div>
          </div>

          {/* Gift Cards Callout */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-12"
          >
            <Card className="bg-gradient-to-r from-purple-50 to-teal-50 border-2 border-purple-200">
              <CardContent className="p-6 sm:p-8">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-start gap-4 text-left">
                    <div className="p-3 bg-gradient-to-r from-purple-600 to-teal-600 rounded-lg">
                      <Gift className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl sm:text-2xl font-bold mb-2 bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent">
                        Gift Cards Available!
                      </h3>
                      <p className="text-gray-700 mb-2">
                        Perfect gifts! Purchase gift cards for SnuggleCrafters subscriptions.
                      </p>
                      <p className="text-sm text-gray-600">
                        Recipients can redeem during sign-up or from their account.
                      </p>
                    </div>
                  </div>
                  <Link href="/buy-gift-card">
                    <Button 
                      size="lg" 
                      className="bg-gradient-to-r from-purple-600 to-teal-600 hover:from-purple-700 hover:to-teal-700 text-white whitespace-nowrap"
                      data-testid="link-gift-cards-pricing"
                    >
                      Buy Gift Cards
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Character Studio Add-on Section */}
      <section className="py-12 sm:py-20 px-4 bg-gradient-to-br from-rose-50 to-pink-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent">
              Character Studio Premium Add-on
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              Transform your family photos into magical cartoon characters for
              your stories
            </p>
          </div>

          <div className="grid sm:grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 max-w-4xl mx-auto">
            {characterStudioPacks.map((pack, index) => {
              const Icon = pack.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Card
                    className={`h-full hover:shadow-lg transition-shadow duration-300 relative ${pack.popular ? "ring-2 ring-rose-500 shadow-xl scale-105" : ""}`}
                  >
                    {pack.popular && (
                      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                        <Badge className="bg-gradient-to-r from-rose-600 to-pink-600 text-white px-4 py-1">
                          Most Popular
                        </Badge>
                      </div>
                    )}
                    {pack.savings && (
                      <div className="absolute -top-2 -right-2">
                        <Badge
                          variant="secondary"
                          className="bg-green-100 text-green-800"
                        >
                          {pack.savings}
                        </Badge>
                      </div>
                    )}
                    <CardHeader className="text-center">
                      <div
                        className={`w-16 h-16 bg-gradient-to-r ${pack.color} rounded-lg flex items-center justify-center mx-auto mb-4`}
                      >
                        <Icon className="h-8 w-8 text-white" />
                      </div>
                      <CardTitle className="text-2xl">{pack.name}</CardTitle>
                      <div className="mt-4">
                        <span className="text-4xl font-bold">{pack.price}</span>
                        <span className="text-gray-600 ml-2">
                          {pack.period}
                        </span>
                      </div>
                      <CardDescription className="text-base mt-2">
                        {pack.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-3 mb-8">
                        {pack.features.map((feature, featureIndex) => (
                          <li
                            key={featureIndex}
                            className="flex items-center gap-3"
                          >
                            <Check className="h-5 w-5 text-green-600 flex-shrink-0" />
                            <span className="text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                      <Button
                        className={`w-full ${pack.popular ? "bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-700 hover:to-pink-700" : ""}`}
                        variant={pack.popular ? "default" : "outline"}
                        size="lg"
                      >
                        Add Character Studio
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Additional Child Profile Slots Section */}
      <section className="py-12 sm:py-20 px-4 bg-gradient-to-br from-rose-50 to-pink-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent">
              Additional Child Profile Slots
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              Recurring monthly subscriptions - cancel anytime. First profile FREE with any
              plan!
            </p>
          </div>

          <div className="grid sm:grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 max-w-4xl mx-auto">
            {childProfilePacks.map((pack, index) => {
              const Icon = pack.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  data-testid={`child-profile-pack-${index}`}
                >
                  <Card className="h-full hover:shadow-lg transition-shadow duration-300 relative">
                    <div className="absolute -top-3 -right-3">
                      <Badge className="bg-rose-500 text-white px-3 py-1">
                        RECURRING
                      </Badge>
                    </div>
                    <CardHeader>
                      <div className="w-12 h-12 bg-gradient-to-r from-rose-500 to-pink-500 rounded-lg flex items-center justify-center mb-4">
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                      <CardTitle className="text-xl">{pack.name}</CardTitle>
                      <CardDescription className="text-base">
                        {pack.description}
                      </CardDescription>
                      <div className="text-2xl font-bold text-rose-600 mt-2">
                        {pack.price}
                        <span className="text-sm font-normal text-gray-600 ml-2">{pack.period}</span>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {pack.features.map((feature, featureIndex) => (
                          <li
                            key={featureIndex}
                            className="flex items-center gap-2 text-sm text-gray-600"
                          >
                            <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What Each Child Profile Includes Section */}
      <section className="py-12 sm:py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent">
              What Each Child Profile Includes
            </h2>
          </div>

          <div className="grid sm:grid-cols-1 lg:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              data-testid="child-profile-feature-personal-details"
            >
              <Card className="h-full">
                <CardHeader className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-rose-500 to-pink-500 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <User className="h-8 w-8 text-white" />
                  </div>
                  <CardTitle className="text-xl">Personal Details</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-sm text-gray-600">
                      <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <span>Save child's name, age, birthday</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm text-gray-600">
                      <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <span>Gender and language preferences</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm text-gray-600">
                      <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <span>Auto-fills story wizard</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              data-testid="child-profile-feature-photo-avatar"
            >
              <Card className="h-full">
                <CardHeader className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-rose-500 to-pink-500 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <Camera className="h-8 w-8 text-white" />
                  </div>
                  <CardTitle className="text-xl">Photo & Avatar</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-sm text-gray-600">
                      <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <span>Upload child's photo</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm text-gray-600">
                      <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <span>AI generates cartoon avatar</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm text-gray-600">
                      <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <span>Character appears in all illustrations</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              data-testid="child-profile-feature-story-library"
            >
              <Card className="h-full">
                <CardHeader className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-rose-500 to-pink-500 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <BookOpen className="h-8 w-8 text-white" />
                  </div>
                  <CardTitle className="text-xl">Story Library</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-sm text-gray-600">
                      <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <span>Dedicated story library per child</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm text-gray-600">
                      <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <span>Personalized recommendations</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm text-gray-600">
                      <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <span>Track favorites and reading progress</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Illustration Packs Section */}
      <section className="py-12 sm:py-20 px-4 bg-gradient-to-br from-purple-50 to-teal-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent">
              Illustration Packs
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              Recurring monthly subscriptions to add more AI-generated illustrations - cancel anytime
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {illustrationPacks.map((pack, index) => {
              const Icon = pack.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Card className="h-full hover:shadow-lg transition-shadow duration-300">
                    <CardHeader>
                      <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center mb-4">
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                      <CardTitle className="text-xl">{pack.name}</CardTitle>
                      <CardDescription className="text-base">
                        {pack.description}
                      </CardDescription>
                      <div className="text-2xl font-bold text-purple-600 mt-2">
                        {pack.price}
                        <span className="text-sm font-normal text-gray-600 ml-2">{pack.period}</span>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {pack.features.map((feature, featureIndex) => (
                          <li
                            key={featureIndex}
                            className="flex items-center gap-2 text-sm text-gray-600"
                          >
                            <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Storage Packs Section */}
      <section className="py-12 sm:py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent">
              Storage Packs
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              Recurring monthly subscriptions to expand your cloud storage - cancel anytime
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {storagePacks.map((pack, index) => {
              const Icon = pack.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Card className="h-full hover:shadow-lg transition-shadow duration-300">
                    <CardHeader>
                      <div className="w-12 h-12 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-lg flex items-center justify-center mb-4">
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                      <CardTitle className="text-xl">{pack.name}</CardTitle>
                      <CardDescription className="text-base">
                        {pack.description}
                      </CardDescription>
                      <div className="text-2xl font-bold text-teal-600 mt-2">
                        {pack.price}
                        <span className="text-sm font-normal text-gray-600 ml-2">{pack.period}</span>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {pack.features.map((feature, featureIndex) => (
                          <li
                            key={featureIndex}
                            className="flex items-center gap-2 text-sm text-gray-600"
                          >
                            <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* White-Label Licensing Section */}
      <section className="py-12 sm:py-20 px-4 bg-gradient-to-br from-blue-50 to-cyan-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
              White-Label Licensing
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              Rebrand our platform for your business with custom domains and
              branding
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {whiteLabelPlans.map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card
                  className={`h-full relative ${plan.popular ? "ring-2 ring-blue-500 shadow-xl scale-105" : ""}`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <Badge className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-4 py-1">
                        Most Popular
                      </Badge>
                    </div>
                  )}
                  {plan.savings && (
                    <div className="absolute -top-2 -right-2">
                      <Badge
                        variant="secondary"
                        className="bg-green-100 text-green-800"
                      >
                        {plan.savings}
                      </Badge>
                    </div>
                  )}
                  <CardHeader className="text-center">
                    <div
                      className={`w-16 h-16 bg-gradient-to-r ${plan.color} rounded-lg flex items-center justify-center mx-auto mb-4`}
                    >
                      <Building className="h-8 w-8 text-white" />
                    </div>
                    <CardTitle className="text-2xl">{plan.name}</CardTitle>
                    <div className="mt-4">
                      <span className="text-4xl font-bold">{plan.price}</span>
                      <span className="text-gray-600 ml-2">{plan.period}</span>
                    </div>
                    <CardDescription className="text-base mt-2">
                      {plan.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3 mb-8">
                      {plan.features.map((feature, featureIndex) => (
                        <li
                          key={featureIndex}
                          className="flex items-center gap-3"
                        >
                          <Check className="h-5 w-5 text-green-600 flex-shrink-0" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button className="w-full" variant="outline" size="lg">
                      {plan.cta}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* API Licensing Section */}
      <section className="py-12 sm:py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
              API Licensing
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              Integrate our AI storytelling capabilities into your applications
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {apiPlans.map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card
                  className={`h-full relative ${plan.popular ? "ring-2 ring-green-500 shadow-xl scale-105" : ""}`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <Badge className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-4 py-1">
                        Most Popular
                      </Badge>
                    </div>
                  )}
                  {plan.savings && (
                    <div className="absolute -top-2 -right-2">
                      <Badge
                        variant="secondary"
                        className="bg-green-100 text-green-800"
                      >
                        {plan.savings}
                      </Badge>
                    </div>
                  )}
                  <CardHeader className="text-center">
                    <div
                      className={`w-16 h-16 bg-gradient-to-r ${plan.color} rounded-lg flex items-center justify-center mx-auto mb-4`}
                    >
                      <Code className="h-8 w-8 text-white" />
                    </div>
                    <CardTitle className="text-2xl">{plan.name}</CardTitle>
                    <div className="mt-4">
                      <span className="text-4xl font-bold">{plan.price}</span>
                      <span className="text-gray-600 ml-2">{plan.period}</span>
                    </div>
                    <CardDescription className="text-base mt-2">
                      {plan.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3 mb-8">
                      {plan.features.map((feature, featureIndex) => (
                        <li
                          key={featureIndex}
                          className="flex items-center gap-3"
                        >
                          <Check className="h-5 w-5 text-green-600 flex-shrink-0" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button
                      className={`w-full ${plan.popular ? "bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700" : ""}`}
                      variant={plan.popular ? "default" : "outline"}
                      size="lg"
                    >
                      {plan.cta}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 sm:py-20 px-4 bg-white">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent">
              Pricing FAQ
            </h2>
            <p className="text-lg sm:text-xl text-gray-600">
              Common questions about our pricing and billing
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card>
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-lg mb-2">
                      {faq.question}
                    </h3>
                    <p className="text-gray-700">{faq.answer}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-20 px-4 bg-gradient-to-r from-purple-600 to-teal-600">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Start Your Free Trial?
          </h2>
          <p className="text-lg sm:text-xl text-purple-100 mb-6 sm:mb-8">
            Join thousands of families who have discovered the magic of
            personalized storytelling
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/auth">
              <Button
                size="lg"
                variant="outline"
                className="text-lg px-6 sm:px-8 py-4 sm:py-6 border-white text-white hover:bg-white hover:text-purple-600 w-full sm:w-auto"
              >
                Start Your Free Trial
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link href="/features">
              <Button
                size="lg"
                variant="outline"
                className="text-lg px-6 sm:px-8 py-4 sm:py-6 border-white text-white hover:bg-white hover:text-purple-600 w-full sm:w-auto"
              >
                View Features
              </Button>
            </Link>
          </div>
          <p className="text-purple-200 mt-4 sm:mt-6 text-sm">
            No credit card required • 7-day free trial • Cancel anytime
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
